// -----------------------------------------------------------------------------
// ui/panels/text.ts — add and edit text overlays.
//
// "Add text" drops a text clip on a text track at the playhead. When a text clip
// is selected, an editor appears: content, size, colour and on-screen position.
// Edits give LIVE preview (the model is updated on `input` so the engine redraws),
// but commit a single undoable command on `change` — so typing a word is one undo
// step, not one-per-keystroke. (CLAUDE.md §6 Tier 0: "add a text overlay".)
// -----------------------------------------------------------------------------
import { AddClipCommand, type TextPatch, UpdateTextClipCommand } from '../../core/commands/clip-commands';
import { AddTrackCommand } from '../../core/commands/track-commands';
import { type TextClip, findTrack, locateClip, newId } from '../../core/model';
import type { EditorContext } from '../context';
import { clear, el } from '../dom';

export function createTextPanel(ctx: EditorContext): HTMLElement {
  const { store, player } = ctx;

  const editor = el('div', {});
  const panel = el(
    'div',
    { class: 'zf-panel' },
    el('h2', {}, 'Text'),
    el('p', { class: 'zf-panel-hint' }, 'Add a text overlay, then drag it on the timeline to position it in time.'),
    el('button', { class: 'zf-btn zf-btn-primary', onclick: addText }, '+ Add text'),
    editor,
  );

  let lastSelection: string | null | undefined;
  renderEditor();
  // Only rebuild the editor when the SELECTION changes — not on every state
  // change — so live typing doesn't yank focus out of the inputs.
  store.subscribe(() => {
    if (store.state.selectedClipId !== lastSelection) renderEditor();
  });

  function addText(): void {
    const project = store.state.project;
    let track = project.tracks.find((t) => t.kind === 'text');
    if (!track) {
      const addTrack = new AddTrackCommand('text', 'Text 1');
      store.dispatch(addTrack);
      track = findTrack(project, addTrack.trackId);
    }
    if (!track) return;
    const clip: TextClip = {
      id: newId(),
      kind: 'text',
      name: 'Text',
      startSec: player.getTime(),
      durationSec: 3,
      text: 'Your text',
      fontSize: Math.round(project.height * 0.09),
      color: '#ffffff',
      fontFamily: 'sans-serif',
      xFrac: 0.5,
      yFrac: 0.5,
    };
    store.dispatch(new AddClipCommand(track.id, clip));
    store.select(clip.id);
    ctx.setStatus('Text added — edit it below or drag it on the timeline.');
  }

  function selectedText(): TextClip | null {
    const id = store.state.selectedClipId;
    if (!id) return null;
    const loc = locateClip(store.state.project, id);
    return loc && loc.clip.kind === 'text' ? loc.clip : null;
  }

  function renderEditor(): void {
    lastSelection = store.state.selectedClipId;
    clear(editor);
    const clip = selectedText();
    if (!clip) {
      editor.append(el('p', { class: 'zf-panel-hint' }, 'Select a text clip to edit it.'));
      return;
    }

    const textArea = el('textarea', { class: 'zf-textarea' }) as HTMLTextAreaElement;
    textArea.value = clip.text;
    bindLive(textArea, clip.id, 'text', (v) => v);

    const size = el('input', { type: 'range', min: '8', max: '400', step: '1' }) as HTMLInputElement;
    size.value = String(clip.fontSize);
    bindLive(size, clip.id, 'fontSize', (v) => Number(v));

    const color = el('input', { type: 'color' }) as HTMLInputElement;
    color.value = normalizeHex(clip.color);
    bindLive(color, clip.id, 'color', (v) => v);

    const xPos = el('input', { type: 'range', min: '0', max: '100', step: '1' }) as HTMLInputElement;
    xPos.value = String(Math.round(clip.xFrac * 100));
    bindLive(xPos, clip.id, 'xFrac', (v) => Number(v) / 100);

    const yPos = el('input', { type: 'range', min: '0', max: '100', step: '1' }) as HTMLInputElement;
    yPos.value = String(Math.round(clip.yFrac * 100));
    bindLive(yPos, clip.id, 'yFrac', (v) => Number(v) / 100);

    editor.append(
      field('Text', textArea),
      field('Size', size),
      field('Colour', color),
      el('div', { class: 'zf-row' }, field('Position X', xPos), field('Position Y', yPos)),
    );
  }

  // Wire an input for live preview + single-command undo. `parse` turns the input
  // string into the model value.
  function bindLive(
    input: HTMLInputElement | HTMLTextAreaElement,
    clipId: string,
    key: keyof Pick<TextClip, 'text' | 'fontSize' | 'color' | 'xFrac' | 'yFrac'>,
    parse: (raw: string) => unknown,
  ): void {
    let original: unknown;
    const getClip = (): TextClip | null => {
      const loc = locateClip(store.state.project, clipId);
      return loc && loc.clip.kind === 'text' ? loc.clip : null;
    };
    input.addEventListener('input', () => {
      const clip = getClip();
      if (!clip) return;
      if (original === undefined) original = clip[key]; // capture pre-edit value once
      (clip as unknown as Record<string, unknown>)[key] = parse(input.value);
      store.notify(); // live preview, no undo entry yet
    });
    input.addEventListener('change', () => {
      const clip = getClip();
      if (!clip) return;
      const value = clip[key];
      if (original === undefined) original = value;
      if (Object.is(value, original)) {
        original = undefined;
        return;
      }
      // Restore, then commit one undoable command for the whole edit.
      (clip as unknown as Record<string, unknown>)[key] = original;
      const patch: TextPatch = {};
      (patch as Record<string, unknown>)[key] = value;
      store.dispatch(new UpdateTextClipCommand(clipId, patch));
      original = undefined;
    });
  }

  return panel;
}

function field(label: string, control: HTMLElement): HTMLElement {
  return el('label', { class: 'zf-field' }, el('span', {}, label), control);
}

// Coerce a CSS colour to a #rrggbb value the <input type=color> accepts.
function normalizeHex(color: string): string {
  return /^#[0-9a-f]{6}$/i.test(color) ? color : '#ffffff';
}
